import React from "react";
import { toast } from "react-hot-toast";

import FleetService from "@/services/FleetService";

export interface DriverFormData {
  address: string;
  assigned_vehicle_id: any;
  email: string;
  full_name: string;
  id: string;
  licenseExpiry: string;
  licenseNumber: string;
  phone: string;
  status: "active" | "inactive" | "suspended";
  vehicle?:
    | string
    | Vehicle
    | {
        brand?: string;
        id?: string;
        licensePlate?: string;
        make?: string;
        model?: string;
        plate_number?: string;
      };
}

interface AddDriverModalProps {
  onClose: () => void;
  onSubmit: (data: DriverFormData) => void;
  open: boolean;
}

interface Vehicle {
  assignedDriver: string;
  brand: string;
  id: string;
  model: string;
  plate: string;
}

const AddDriverModal: React.FC<AddDriverModalProps> = ({
  onClose,
  onSubmit,
  open,
}) => {
  const [form, setForm] = React.useState<DriverFormData>({
    address: "",
    assigned_vehicle_id: "",
    email: "",
    full_name: "",
    id: "",
    licenseExpiry: "",
    licenseNumber: "",
    phone: "",
    status: "active",
    vehicle: "",
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [vehicles, setVehicles] = React.useState<Vehicle[]>([]);
  const [loadingVehicles, setLoadingVehicles] = React.useState(false);

  // Load vehicles when modal opens
  React.useEffect(() => {
    if (open) {
      loadVehicles();
    }
  }, [open]);

  const loadVehicles = async () => {
    try {
      setLoadingVehicles(true);
      const response = await FleetService.getVehicles();
      if (Array.isArray(response)) {
        // Show all vehicles, not just unassigned
        const availableVehicles = response.map((v: any) => ({
          assignedDriver: v.assigned_driver || v.assignedDriver,
          brand: v.brand || v.make || "",
          id: v.id?.toString() || "",
          model: v.model || "",
          plate: v.plate_number || v.plate || v.licensePlate || "",
        }));
        setVehicles(availableVehicles);
      }
    } catch (error) {
      console.error("Error loading vehicles:", error);
    } finally {
      setLoadingVehicles(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-gray-900 md:max-w-3xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "90vh", overflowY: "auto", width: "80vw" }}
      >
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          Add Driver
        </h2>
        <form
          className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2"
          onSubmit={async (e) => {
            e.preventDefault();
            setSubmitting(true);
            try {
              const {
                address,
                email,
                full_name,
                licenseExpiry,
                licenseNumber,
                phone,
                status,
                vehicle,
              } = form;
              const payload: any = {
                address,
                email,
                full_name,
                license_expiry: licenseExpiry,
                license_number: licenseNumber,
                phone,
                status,
              };
              if (vehicle) {
                payload.vehicle = vehicle;
              }
              // Send to backend
              await FleetService.createDriver(payload);
              toast.success("Driver added successfully!");
              onSubmit(form);
              setForm({
                address: "",
                assigned_vehicle_id: "",
                email: "",
                full_name: "",
                id: "",
                licenseExpiry: "",
                licenseNumber: "",
                phone: "",
                status: "active",
                vehicle: "",
              });
              onClose();
              onClose();
            } catch (error: any) {
              if (error.response && error.response.data) {
                toast.error(
                  error.response.data.message +
                    "\n" +
                    Object.entries(error.response.data.errors || {})
                      .map(
                        ([field, msgs]) =>
                          `${field}: ${Array.isArray(msgs) ? msgs.join(", ") : msgs}`,
                      )
                      .join("\n"),
                );
              } else {
                toast.error("Failed to add driver.");
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          <InputField
            className="w-full"
            id="driver-name"
            label="Full Name"
            onChange={(value) => setForm({ ...form, full_name: value })}
            value={form.full_name}
          />
          <InputField
            className="w-full"
            id="driver-email"
            label="Email"
            onChange={(value) => setForm({ ...form, email: value })}
            type="email"
            value={form.email}
          />
          <InputField
            className="w-full"
            id="driver-phone"
            label="Phone"
            onChange={(value) => setForm({ ...form, phone: value })}
            value={form.phone}
          />
          <InputField
            className="w-full"
            id="driver-address"
            label="Address"
            onChange={(value) => setForm({ ...form, address: value })}
            value={form.address}
          />
          <InputField
            className="w-full"
            id="driver-license"
            label="License Number"
            onChange={(value) => setForm({ ...form, licenseNumber: value })}
            value={form.licenseNumber}
          />
          <InputField
            className="w-full"
            id="driver-license-expiry"
            label="License Expiry"
            onChange={(value) => setForm({ ...form, licenseExpiry: value })}
            type="date"
            value={form.licenseExpiry}
          />
          {/* Vehicle Dropdown (optional) */}
          <div className="relative md:col-span-2">
            <select
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              disabled={loadingVehicles}
              id="driver-vehicle"
              onChange={(e) => setForm({ ...form, vehicle: e.target.value })}
              value={
                typeof form.vehicle === "string"
                  ? form.vehicle
                  : form.vehicle?.id || ""
              }
            >
              <option value="">
                {loadingVehicles ? "Loading vehicles..." : "Select a vehicle"}
              </option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.brand} {v.model} ({v.plate})
                </option>
              ))}
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="driver-vehicle"
            >
              Assign Vehicle (optional)
            </label>
          </div>
          {/* Status */}
          <div className="relative md:col-span-2">
            <select
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="driver-status"
              onChange={(e) =>
                setForm({
                  ...form,
                  status: e.target.value as DriverFormData["status"],
                })
              }
              required
              value={form.status}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="driver-status"
            >
              Status
            </label>
          </div>

          <div className="mt-4 flex justify-end gap-2 md:col-span-2">
            <button
              className="rounded bg-gray-200 px-4 py-2 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className="rounded bg-blue-600 px-4 py-2 text-white dark:bg-blue-500"
              disabled={submitting}
              type="submit"
            >
              {submitting ? "Adding..." : "Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const InputField = ({
  className,
  id,
  inputProps,
  label,
  onChange,
  type = "text",
  value,
}: {
  className?: string;
  id: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  label: string;
  onChange: (value: string) => void;
  type?: string;
  value: string;
}) => (
  <div className={`relative ${className || ""}`}>
    <input
      className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
      id={id}
      onChange={(e) => onChange(e.target.value)}
      required
      type={type}
      value={value}
      {...(inputProps || {})}
    />
    <label
      className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
      htmlFor={id}
    >
      {label}
    </label>
  </div>
);

export default AddDriverModal;
